import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import * as crawlerApi from 'api/crawlerApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/crawlerReducer/registrationSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { useListStore as useApiKeyTemplateListStore } from '../../ApiKeyTemplatePage/hooks';
import {
  CrawlerRegistrationFormFields,
  createCrawlerRegistrationFormFields,
  mapFormFieldsToCrawlerRegistrationPayload,
} from '../types/crawlerRegistrationFormFields';
import { useListStore } from './useListStore';

/**
 * クローラー登録 store の実装をコンポーネントから隠すための中間層
 */
export const useRegistrationStore = () => {
  const dispatch = useAppDispatch();
  const { fetchCrawlers } = useListStore();
  const toast = useToast();

  const form = useForm<CrawlerRegistrationFormFields>({
    defaultValues: createCrawlerRegistrationFormFields({}),
    mode: 'all',
  });
  const { getValues, reset, handleSubmit } = form;

  const isCheckUnnecessaryApiKey = useAppSelector(
    registrationSelectors.isCheckUnnecessaryApiKey,
  );

  const setIsCheckUnnecessaryApiKey = useCallback(() => {
    dispatch(registrationActions.changeIsCheckUnnecessaryApiKey());
    reset({ apiKeyTemplateId: '' });
  }, [dispatch, reset]);

  const isOpenModal = useAppSelector(registrationSelectors.isOpenModal);

  const currentModalStep = useAppSelector(
    registrationSelectors.currentModalStep,
  );

  const { apiKeyTemplates, fetchApiKeyTemplates } =
    useApiKeyTemplateListStore();

  const onOpenModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
    void fetchApiKeyTemplates();
  }, [dispatch, fetchApiKeyTemplates]);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchCrawlers();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: CrawlerRegistrationFormFields) => {
      dispatch(loaderActions.setLoading(true));
      const result = await crawlerApi.addCrawler(
        mapFormFieldsToCrawlerRegistrationPayload(values),
      );
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: 'クローラーの登録に失敗しました。',
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'クローラーの登録に失敗しました。',
      });
    },
  );

  return {
    /** クローラー登録モーダル表示状態 */
    isOpenModal,
    /** クローラー登録モーダルの現ステップ */
    currentModalStep,
    /** モーダル開く */
    onOpenModal,
    /** モーダル閉じる */
    onCloseModal,
    /** 登録完了後モーダル閉じる */
    onCloseModalWhenCompleted,
    /** 確認画面へ */
    onConfirm,
    /** 入力画面へ戻る */
    onGoBack,
    /** 入力を送信 */
    onSubmit,
    /** フォーム */
    form,
    /** フォーム values */
    values: getValues(),
    /** APIキーテンプレート */
    apiKeyTemplates,
    /** 選択中APIキーテンプレート */
    selectedApiKeyTemplate:
      apiKeyTemplates.find(
        ({ id }) => id?.toString() === getValues().apiKeyTemplateId,
      ) || null,
    /** APIキーテンプレート不要チェック */
    isCheckUnnecessaryApiKey,
    setIsCheckUnnecessaryApiKey,
  };
};
